import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import { Check, ImageOff, Search, X } from "lucide-react";
import { SelectDropdown, TextInput, showErrorAlert } from "../../../../components/ui/index.js";
import getMediaUrl from "../../../../components/tools/utils.jsx";
import { PRODUCT_TYPE_FILTERS, formatPrice } from "../../../../components/catalog/catalogUtils.js";

const TABS = [
    { id: "designs", label: "Diseños" },
    { id: "products", label: "Stock de la tienda" },
];

function useOptions(endpoint, params) {
    const [state, setState] = useState({ loading: true, error: false, results: [] });
    const key = JSON.stringify(params);
    useEffect(() => {
        let cancelled = false;
        setState((prev) => ({ ...prev, loading: true, error: false }));
        const timer = setTimeout(() => {
            axios.get(endpoint, { params: JSON.parse(key) })
                .then(({ data }) => { if (!cancelled) setState({ loading: false, error: false, results: data.results || [] }); })
                .catch(() => { if (!cancelled) setState({ loading: false, error: true, results: [] }); });
        }, 250);
        return () => { cancelled = true; clearTimeout(timer); };
    }, [endpoint, key]);
    return state;
}

/**
 * Elegir modelos para el proyecto (C3D-38): diseños de los customizadores
 * (filtrables por tipo) y productos de stock, varios a la vez.
 */
export default function AddItemsModal({ projectId, existing, onClose, onAdded }) {
    const [tab, setTab] = useState("designs");
    const [search, setSearch] = useState("");
    const [productType, setProductType] = useState("");
    const [selected, setSelected] = useState({});
    const [saving, setSaving] = useState(false);

    const designs = useOptions("/projects/design-options/", { search: search || undefined, product_type: productType || undefined });
    const products = useOptions("/projects/product-options/", { search: search || undefined });
    const current = tab === "designs" ? designs : products;

    const keyOf = (kind, id) => `${kind}:${id}`;
    const toggle = (kind, option) => setSelected((prev) => {
        const key = keyOf(kind, option.id);
        const next = { ...prev };
        if (next[key]) delete next[key]; else next[key] = { kind, id: option.id };
        return next;
    });
    const selectedCount = Object.keys(selected).length;

    useEffect(() => {
        const onKey = (event) => { if (event.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const add = async () => {
        setSaving(true);
        try {
            const items = Object.values(selected).map(({ kind, id }) => (
                kind === "design" ? { design_id: id } : { product_id: id }
            ));
            await axios.post(`/projects/${projectId}/items/`, { items });
            onAdded();
        } catch (error) {
            showErrorAlert({ text: error?.response?.data?.detail || "No se han podido añadir los modelos." });
        } finally {
            setSaving(false);
        }
    };

    const kind = tab === "designs" ? "design" : "product";

    return (
        <div className="modal modal-open" role="dialog" aria-modal="true" aria-labelledby="add-items-title">
            <div className="modal-box max-w-4xl w-[calc(100%-2rem)] p-0 flex flex-col max-h-[90dvh]">
                <div className="flex items-center justify-between gap-3 p-4 border-b border-base-content/10">
                    <h2 id="add-items-title" className="text-lg font-semibold">Añadir modelos</h2>
                    <button type="button" className="btn btn-ghost btn-sm btn-square" onClick={onClose} aria-label="Cerrar">
                        <X className="w-5 h-5" aria-hidden="true" />
                    </button>
                </div>

                <div className="p-4 flex flex-col gap-3 border-b border-base-content/10">
                    <div role="tablist" className="tabs tabs-boxed self-start">
                        {TABS.map((item) => (
                            <button
                                key={item.id}
                                type="button"
                                role="tab"
                                aria-selected={tab === item.id}
                                className={`tab ${tab === item.id ? "tab-active" : ""}`}
                                onClick={() => setTab(item.id)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <TextInput
                            leftIcon={<Search className="w-4 h-4" />}
                            placeholder={tab === "designs" ? "Buscar por nombre o email del dueño" : "Buscar producto"}
                            aria-label="Buscar"
                            value={search}
                            onChange={(event) => setSearch(event.target.value)}
                            inputSize="sm"
                        />
                        {tab === "designs" && (
                            <SelectDropdown
                                options={PRODUCT_TYPE_FILTERS}
                                value={productType}
                                onChange={setProductType}
                                size="sm"
                                className="sm:w-56"
                            />
                        )}
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                    {current.loading && (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" aria-busy="true">
                            {[0, 1, 2, 3].map((key) => <div key={key} className="skeleton h-44 rounded-xl" />)}
                        </div>
                    )}
                    {!current.loading && current.error && (
                        <p role="alert" className="text-sm text-error">No se ha podido cargar la lista. Cambia la búsqueda para reintentar.</p>
                    )}
                    {!current.loading && !current.error && current.results.length === 0 && (
                        <p className="text-sm text-base-content/70 text-center py-10">
                            {tab === "designs" ? "No hay diseños con esa búsqueda. Créalos en los customizadores y vuelve aquí." : "No hay productos con esa búsqueda."}
                        </p>
                    )}
                    {!current.loading && !current.error && current.results.length > 0 && (
                        <ul className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
                            {current.results.map((option) => {
                                const isSelected = Boolean(selected[keyOf(kind, option.id)]);
                                const alreadyIn = existing.has(keyOf(kind, option.id));
                                return (
                                    <li key={option.id}>
                                        <button
                                            type="button"
                                            onClick={() => toggle(kind, option)}
                                            aria-pressed={isSelected}
                                            className={`relative w-full text-left flex flex-col rounded-xl border overflow-hidden transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary ${isSelected ? "border-primary ring-2 ring-primary/30" : "border-base-content/10 hover:border-base-content/30"}`}
                                        >
                                            <div className="aspect-square bg-base-200 flex items-center justify-center">
                                                {option.image_url
                                                    ? <img src={getMediaUrl(option.image_url)} alt="" className="w-full h-full object-contain p-3" loading="lazy" />
                                                    : <ImageOff className="w-8 h-8 text-base-content/30" aria-hidden="true" />}
                                            </div>
                                            <div className="p-2 flex flex-col gap-0.5">
                                                <span className="text-sm font-medium line-clamp-2">{option.name}</span>
                                                <span className="text-xs text-base-content/60 truncate">
                                                    {tab === "designs" ? `${option.product_label} · ${option.owner_email}` : `${formatPrice(option.price)} · stock ${option.stock}`}
                                                </span>
                                                {alreadyIn && <span className="text-xs text-info">Ya está en el proyecto</span>}
                                            </div>
                                            {isSelected && (
                                                <span className="absolute top-2 right-2 rounded-full bg-primary text-primary-content p-1">
                                                    <Check className="w-3.5 h-3.5" aria-hidden="true" />
                                                </span>
                                            )}
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                <div className="flex items-center justify-between gap-3 p-4 border-t border-base-content/10">
                    <span className="text-sm text-base-content/70">
                        {selectedCount ? `${selectedCount} seleccionado${selectedCount === 1 ? "" : "s"}` : "Elige uno o varios"}
                    </span>
                    <div className="flex gap-2">
                        <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancelar</button>
                        <button type="button" className="btn btn-primary btn-sm" onClick={add} disabled={!selectedCount || saving}>
                            {saving && <span className="loading loading-spinner loading-xs" aria-hidden="true" />}
                            Añadir al proyecto
                        </button>
                    </div>
                </div>
            </div>
            <button type="button" className="modal-backdrop" onClick={onClose} aria-label="Cerrar" />
        </div>
    );
}

AddItemsModal.propTypes = {
    projectId: PropTypes.number.isRequired,
    existing: PropTypes.instanceOf(Set).isRequired,
    onClose: PropTypes.func.isRequired,
    onAdded: PropTypes.func.isRequired,
};
